<script module lang="ts">
    import type { Field } from '@/types/types';
    import type { Option } from '@/lib/optionsLists';

    export type Props = {
        type?: 'single' | 'multiple';
        label?: string;
        labelDir?: 'top' | 'left';
        value?: string | undefined;
        error?: string | null;
        options?: Option[];
        name?: string;
        onlyOptionSelected?: boolean;
        oninput?: (e: InputEvent) => any;
        field?: Field;
    };
</script>

<script lang="ts">
    import * as Select from '@/components/ui/select/index.js';
    import InputError from '@/components/InputError.svelte';
    import { Label } from '@/components/ui/label/index.js';
    import { cn } from '@/lib/utils';
    import { parseFieldOptions } from '@/lib/fieldUtils';
    import { createFieldValidation, isFieldRequired } from '@/lib/fieldValidation';

    let {
        type = 'single',
        label,
        labelDir = 'top',
        value = $bindable(),
        error = null,
        options,
        name = '',
        onlyOptionSelected = false,
        oninput = $bindable(),
        field,
        ...args
    }: Props = $props();

    let isDirty = $state(false);

    const computedLabel = $derived(field?.label ?? label);
    const computedOptions = $derived.by(() => {
        if (field?.options) {
            return parseFieldOptions(field);
        }
        return options ?? [];
    });
    const computedName = $derived(field?.column_name ?? name);
    const selectLabel = $derived(computedOptions.find((option: any) => option.value === value)?.label ?? 'Select');

    // Get validation state
    const validation = $derived.by(() => {
        return createFieldValidation({
            field,
            ui_type: field?.ui_type ?? 'select',
            input_type: field?.input_type,
            value,
            isDirty,
        });
    });

    // Compute CSS classes - include server errors
    const validationClasses = $derived(() => {
        if (error) {
            return 'input-error';
        }
        return validation.classes;
    });

    // Auto-select if only one option is available
    $effect(() => {
        if (onlyOptionSelected && Array.isArray(computedOptions) && computedOptions?.length === 1) {
            const optionValue = computedOptions[0].value;
            if (optionValue !== null && optionValue !== undefined) {
                value = optionValue;
            }
        }
    });

    // Mark as dirty when value changes
    $effect(() => {
        if (value !== undefined && value !== null) {
            isDirty = true;
        }
    });

    // Mark as dirty when dropdown closes
    function handleOpenChange(open: boolean) {
        if (!open && !isDirty) {
            isDirty = true;
        }
    }
</script>

<div class="flex min-w-24 w-full flex-col gap-2 {args.class}">
    {#if computedLabel && labelDir === 'top'}
        <Label for={computedLabel}
            >{computedLabel}
            {#if validation.isRequired === true}<span class="ml-1 text-red-500">*</span>{/if}</Label
        >
    {/if}
    <div class={cn('flex min-w-16 gap-2', labelDir === 'top' ? ['flex-col'] : ['items-center'])}>
        {#if computedLabel && labelDir === 'left'}
            <Label for={computedLabel}>
                {computedLabel}
                {#if validation.isRequired === true}<span class="ml-1 text-red-500">*</span>{/if}
            </Label>
        {/if}

        <Select.Root {type} name={computedName} bind:value onOpenChange={handleOpenChange}>
            <Select.Trigger class={cn('w-full', validationClasses())}>
                {selectLabel}
            </Select.Trigger>
            <Select.Content>
                <Select.Group>
                    {#each computedOptions as option (option.value)}
                        <Select.Item
                            value={option.value}
                            label={option.label}
                            class="flex"
                            selected={onlyOptionSelected}
                        >
                            <span class:text-gray-400={option.value === null}>
                                {option.label}
                                {#if option.description}
                                    <span class="text-xs text-gray-500">({option.description})</span
                                    >
                                {/if}
                            </span>
                        </Select.Item>
                    {/each}
                </Select.Group>
            </Select.Content>
        </Select.Root>
    </div>
    {#if validation.errorMessage}
        <InputError message={validation.errorMessage} />
    {:else}
        <InputError message={error} />
    {/if}
</div>
